import React, { useState, useRef, useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import Typography from '../Typography';
import Button from '../Button';
import { FlexColumn, FlexRow } from '../GridsAndLayouts/GridsAndLayouts';
import { CheckIcon, ExclamationCircleIcon, EyeIcon, EyeOffIcon } from '../Icons';
import './Input.scss';

/** Digits and at most one `.` — used when `amount` is true (`inputMode` alone does not block letters). */
const sanitizeDecimalAmountString = (raw) => {
  const s = String(raw).replace(/[^\d.]/g, '');
  const dot = s.indexOf('.');
  if (dot === -1) return s;
  return `${s.slice(0, dot + 1)}${s.slice(dot + 1).replace(/\./g, '')}`;
};

/** National number segment — digits only (country code lives in affix / separate control). */
const sanitizePhoneDigitsString = (raw) => String(raw).replace(/\D/g, '');

const Input = React.forwardRef(
  (
    {
      type = 'text',
      placeholder = '',
      value,
      defaultValue,
      onChange,
      onBlur,
      onFocus,
      disabled = false,
      readOnly = false,
      error = false,
      success = false,
      /** When true, only digits and one decimal point are accepted (currency-style amount). */
      amount = false,
      /** When true, only digits are accepted (phone national number after country prefix). */
      phone = false,
      inputMode,
      className = '',
      id,
      name,
      ...props
    },
    ref
  ) => {
    const handleChange = (e) => {
      if (!amount && !phone) {
        if (onChange) onChange(e);
        return;
      }
      const raw = e.target.value;
      const sanitized = phone ? sanitizePhoneDigitsString(raw) : sanitizeDecimalAmountString(raw);
      if (value !== undefined && value !== null) {
        if (sanitized !== raw) {
          onChange?.({
            ...e,
            target: { ...e.target, value: sanitized },
            currentTarget: e.currentTarget,
          });
        } else if (onChange) {
          onChange(e);
        }
        return;
      }
      if (sanitized !== raw) {
        e.target.value = sanitized;
      }
      if (onChange) onChange(e);
    };

    const classNames = [
      't17',
      'ds-input',
      error && 'ds-input--error',
      success && !error && 'ds-input--success',
      disabled && 'ds-input--disabled',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    let resolvedInputMode = inputMode;
    if (phone) {
      resolvedInputMode = 'numeric';
    } else if (amount) {
      resolvedInputMode = 'decimal';
    }

    /* Leaf control: native `<input>` for refs, autofill, and all `type` values. */
    return (
      <input
        ref={ref}
        type={type}
        id={id}
        name={name}
        className={classNames}
        placeholder={placeholder}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={disabled}
        readOnly={readOnly}
        inputMode={resolvedInputMode}
        {...props}
      />
    );
  }
);

Input.displayName = 'Input';

Input.propTypes = {
  type: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.bool,
  success: PropTypes.bool,
  amount: PropTypes.bool,
  phone: PropTypes.bool,
  inputMode: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
};

Input.defaultProps = {
  type: 'text',
  placeholder: '',
  value: undefined,
  defaultValue: undefined,
  onChange: undefined,
  onBlur: undefined,
  onFocus: undefined,
  disabled: false,
  readOnly: false,
  error: false,
  success: false,
  amount: false,
  phone: false,
  inputMode: undefined,
  className: '',
  id: undefined,
  name: undefined,
};

const FormField = ({
  label,
  optional = false,
  optionalLabel,
  required = false,
  helperText,
  errorText,
  characterCount,
  success = false,
  children,
  className = '',
  id,
  ...props
}) => {
  const fieldId = id || `field-${Math.random().toString(36).substr(2, 9)}`;
  const optionalText = optionalLabel || 'Optional';
  let counterText = null;
  if (characterCount != null) {
    if (typeof characterCount === 'object' && characterCount.max != null && characterCount.current != null) {
      counterText = `${characterCount.current}/${characterCount.max}`;
    } else if (typeof characterCount === 'string') {
      counterText = characterCount;
    }
  }

  const showFooterRow = (helperText && !errorText) || counterText;
  const helperIsSuccess = Boolean(success && helperText && !errorText);

  return (
    <FlexColumn
      align="start"
      className={[
        'ds-form-field',
        success && !errorText && 'ds-form-field--success',
        errorText && 'ds-form-field--error',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      {label && (
        <FlexRow className="ds-form-field__label-row" justify="between" align="center" fullWidth>
          <Typography variant="t15" component="label" htmlFor={fieldId} className="ds-form-field__label">
            {label}
            {required && (
              <Typography component="span" variant="t15" className="ds-form-field__required" aria-hidden="true">
                *
              </Typography>
            )}
          </Typography>
          {optional && (
            <Typography variant="t15" color="muted" component="span" className="ds-form-field__optional">
              {optionalText}
            </Typography>
          )}
        </FlexRow>
      )}

      <FlexColumn className="ds-form-field__input" align="stretch" fullWidth>
        {children}
      </FlexColumn>

      {errorText && (
        <FlexRow className="ds-form-field__error" role="alert" align="start" fullWidth gap={6}>
          <ExclamationCircleIcon className="ds-form-field__error-icon" size={12} fill="var(--pal-red-1000)" />
          <Typography component="span" variant="bodyMedium" className="ds-form-field__error-text">
            {errorText}
          </Typography>
        </FlexRow>
      )}

      {showFooterRow && !errorText && (
        <FlexRow
          className={['ds-form-field__footer', helperIsSuccess && 'ds-form-field__footer--success']
            .filter(Boolean)
            .join(' ')}
          justify="between"
          align="center"
          fullWidth
          gap={8}
        >
          {helperText ? (
            <Typography
              component="span"
              variant="bodyMedium"
              className={[
                'ds-form-field__helper',
                helperIsSuccess ? 'ds-form-field__helper--success' : 'ds-form-field__helper--neutral',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {helperIsSuccess && <CheckIcon className="ds-form-field__success-icon" size={16} strokeWidth={2.2} />}
              {helperText}
            </Typography>
          ) : (
            <Typography component="span" variant="bodyMedium" className="ds-form-field__footer-spacer" />
          )}
          {counterText ? (
            <Typography component="span" variant="bodyMedium" className="ds-form-field__counter">
              {counterText}
            </Typography>
          ) : null}
        </FlexRow>
      )}
    </FlexColumn>
  );
};

FormField.propTypes = {
  label: PropTypes.string,
  optional: PropTypes.bool,
  optionalLabel: PropTypes.string,
  required: PropTypes.bool,
  helperText: PropTypes.string,
  errorText: PropTypes.string,
  characterCount: PropTypes.oneOfType([
    PropTypes.shape({ current: PropTypes.number, max: PropTypes.number }),
    PropTypes.string,
  ]),
  success: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  id: PropTypes.string,
};

FormField.defaultProps = {
  label: undefined,
  optional: false,
  optionalLabel: undefined,
  required: false,
  helperText: undefined,
  errorText: undefined,
  characterCount: undefined,
  success: false,
  className: '',
  id: undefined,
};

const InputGroup = ({ prefix, suffix, prefixText, suffixText, children, className = '', ...props }) => {
  return (
    <FlexRow className={`ds-input-group ${className}`.trim()} align="stretch" justify="start" fullWidth {...props}>
      {(prefix || prefixText) && (
        <FlexRow className="ds-input-group__prefix" align="center" justify="start" fullWidth={false}>
          {prefixText ? (
            <Typography variant="body1" component="span">
              {prefixText}
            </Typography>
          ) : (
            prefix
          )}
        </FlexRow>
      )}

      <FlexRow className="ds-input-group__input" align="stretch" fullWidth>
        {children}
      </FlexRow>

      {(suffix || suffixText) && (
        <FlexRow className="ds-input-group__suffix" align="center" justify="start" fullWidth={false}>
          {suffixText ? (
            <Typography variant="body1" component="span">
              {suffixText}
            </Typography>
          ) : (
            suffix
          )}
        </FlexRow>
      )}
    </FlexRow>
  );
};

InputGroup.propTypes = {
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  prefixText: PropTypes.string,
  suffixText: PropTypes.string,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

InputGroup.defaultProps = {
  prefix: undefined,
  suffix: undefined,
  prefixText: undefined,
  suffixText: undefined,
  className: '',
};

const Textarea = React.forwardRef(
  (
    {
      placeholder = '',
      value,
      defaultValue,
      onChange,
      onBlur,
      onFocus,
      disabled = false,
      readOnly = false,
      error = false,
      success = false,
      rows = 3,
      className = '',
      id,
      name,
      ...props
    },
    ref
  ) => {
    const classNames = [
      'ds-textarea',
      error && 'ds-textarea--error',
      success && !error && 'ds-textarea--success',
      disabled && 'ds-textarea--disabled',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    /* Leaf control: native `<textarea>` for multiline + ref forwarding. */
    return (
      <textarea
        ref={ref}
        id={id}
        name={name}
        className={classNames}
        placeholder={placeholder}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={disabled}
        readOnly={readOnly}
        rows={rows}
        {...props}
      />
    );
  }
);

Textarea.displayName = 'Textarea';

Textarea.propTypes = {
  placeholder: PropTypes.string,
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.bool,
  success: PropTypes.bool,
  rows: PropTypes.number,
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
};

Textarea.defaultProps = {
  placeholder: '',
  value: undefined,
  defaultValue: undefined,
  onChange: undefined,
  onBlur: undefined,
  onFocus: undefined,
  disabled: false,
  readOnly: false,
  error: false,
  success: false,
  rows: 3,
  className: '',
  id: undefined,
  name: undefined,
};

/* eslint-disable react/require-default-props -- PasswordInput mirrors optional Input API */
const PasswordInput = React.forwardRef(
  (
    {
      id: idProp,
      placeholder = 'Enter Password',
      value,
      defaultValue,
      onChange,
      onBlur,
      onFocus,
      disabled = false,
      error = false,
      success = false,
      className = '',
      name,
      /** Initial visibility of the password (e.g. docs “visible vs masked” columns). */
      defaultVisible = false,
      ...rest
    },
    ref
  ) => {
    const generatedId = useId();
    const fieldId = idProp || generatedId;
    const [visible, setVisible] = useState(Boolean(defaultVisible));

    return (
      <FlexColumn className={['ds-input-password', className].filter(Boolean).join(' ')} align="stretch" fullWidth>
        <InputGroup
          suffix={
            <Button
              variant="tertiary"
              size="sm"
              type="button"
              className="ds-input-password__toggle"
              tabIndex={-1}
              disabled={disabled}
              aria-label={visible ? 'Hide password' : 'Show password'}
              icon={visible ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
              iconPosition="right"
              onClick={() => setVisible((v) => !v)}
            />
          }
        >
          <Input
            ref={ref}
            id={fieldId}
            name={name}
            type={visible ? 'text' : 'password'}
            placeholder={placeholder}
            value={value}
            defaultValue={defaultValue}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            disabled={disabled}
            error={error}
            success={success}
            autoComplete="off"
            {...rest}
            className={['t17', 'ds-input'].filter(Boolean).join(' ')}
          />
        </InputGroup>
      </FlexColumn>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

PasswordInput.propTypes = {
  id: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  success: PropTypes.bool,
  className: PropTypes.string,
  name: PropTypes.string,
  defaultVisible: PropTypes.bool,
};
/* eslint-enable react/require-default-props */

const OtpInput = ({
  length = 5,
  value = '',
  onChange,
  disabled = false,
  error = false,
  success = false,
  className = '',
  id: idProp,
  inputMode = 'numeric',
  autoComplete = 'one-time-code',
}) => {
  const generatedId = useId();
  const baseId = idProp || generatedId;
  const inputRefs = useRef([]);

  const digits = (value || '').replace(/\D/g, '').slice(0, length);
  const chars = Array.from({ length }, (_, i) => digits[i] || '');

  const focusCell = (index) => {
    const el = inputRefs.current[index];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const commitDigits = useCallback(
    (nextRaw) => {
      const next = nextRaw.replace(/\D/g, '').slice(0, length);
      if (onChange) {
        onChange(next);
      }
    },
    [length, onChange]
  );

  const handleChange = (index, event) => {
    const raw = event.target.value.replace(/\D/g, '');
    const chunk = raw.slice(-1) || '';
    const prefix = digits.slice(0, index);
    const suffix = digits.slice(index + 1);
    const next = `${prefix}${chunk}${suffix}`.slice(0, length);
    commitDigits(next);
    if (chunk && index < length - 1) {
      focusCell(index + 1);
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      if (chars[index]) {
        return;
      }
      event.preventDefault();
      const prefix = digits.slice(0, Math.max(0, index - 1));
      const suffix = digits.slice(index);
      commitDigits(prefix + suffix.slice(1));
      if (index > 0) {
        focusCell(index - 1);
      }
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusCell(index - 1);
    }
    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      focusCell(index + 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const text = (event.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, length);
    commitDigits(text);
    focusCell(Math.min(text.length, length - 1));
  };

  return (
    <FlexRow
      className={['ds-otp', error && 'ds-otp--error', success && !error && 'ds-otp--success', className]
        .filter(Boolean)
        .join(' ')}
      align="stretch"
      fullWidth
    >
      {Array.from({ length }, (_, index) => (
        <Input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          id={`${baseId}-${index}`}
          className="ds-otp__cell"
          type="text"
          inputMode={inputMode}
          autoComplete={index === 0 ? autoComplete : 'off'}
          maxLength={1}
          disabled={disabled}
          value={chars[index] || ''}
          aria-label={`Digit ${index + 1} of ${length}`}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={index === 0 ? handlePaste : undefined}
        />
      ))}
    </FlexRow>
  );
};

/* eslint-disable react/require-default-props -- OtpInput optional props mirror field usage */
OtpInput.propTypes = {
  length: PropTypes.number,
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  success: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  inputMode: PropTypes.string,
  autoComplete: PropTypes.string,
};
/* eslint-enable react/require-default-props */

Input.FormField = FormField;
Input.Group = InputGroup;
Input.Textarea = Textarea;
Input.Password = PasswordInput;
Input.Otp = OtpInput;

export { Input, FormField, InputGroup, Textarea, PasswordInput, OtpInput };
export default Input;
